import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  BellOff,
  CheckCircle2,
  Circle,
  Filter,
  Inbox,
  LogOut,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  RefreshCw,
  Send,
  Sparkles,
  X,
  Loader2,
  Lock,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { authClient, formatApiErrorDetail } from "../lib/authClient";
import {
  getPushSubscription,
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush,
} from "../lib/push";
import { useAuth } from "../context/AuthContext";
import { SITE } from "../data/site";
import { usePageMeta } from "../hooks/usePageMeta";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "unread", label: "Unread" },
  { id: "contact", label: "Contact form" },
  { id: "design", label: "Design My Wall" },
];

function timeAgo(iso) {
  const t = new Date(iso).getTime();
  if (!t) return "";
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

export default function AdminDashboard() {
  usePageMeta({
    title: "Owner Dashboard | Swift Luxury Walls",
    description: "Internal admin area.",
    canonicalPath: "/admin",
    noindex: true,
  });
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, summaryRes] = await Promise.all([
        authClient.get("/api/admin/leads"),
        authClient.get("/api/admin/leads/summary"),
      ]);
      setLeads(leadsRes.data.leads || []);
      setSummary(summaryRes.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 30000); // poll every 30s as backup for push
    return () => clearInterval(interval);
  }, [fetchAll]);

  // Auto-prompt & auto-re-enable push notifications after every login
  useEffect(() => {
    (async () => {
      if (!isPushSupported()) return;
      const sub = await getPushSubscription();
      if (sub) {
        setPushOn(true);
        return;
      }
      // No active subscription — try to (re)subscribe.
      // • If permission is 'default' → the browser will prompt.
      // • If permission is 'granted' → silently subscribe (fixes case where
      //   the subscription was cleared but the user already opted in).
      if (Notification.permission === "default" || Notification.permission === "granted") {
        setTimeout(async () => {
          try {
            await subscribeToPush();
            setPushOn(true);
            if (Notification.permission === "default") {
              toast.success("Push notifications enabled — you'll be alerted when new leads come in.");
            }
          } catch (_) {
            // silent — user declined
          }
        }, 800);
      }
    })();
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushOn) {
        await unsubscribeFromPush();
        setPushOn(false);
        toast.success("Push notifications turned off.");
      } else {
        await subscribeToPush();
        setPushOn(true);
        toast.success("Push notifications enabled.");
      }
    } catch (e) {
      toast.error(e.message || "Could not update push settings.");
    } finally {
      setPushBusy(false);
    }
  };

  const testPush = async () => {
    try {
      await sendTestPush();
      toast.success("Test push sent — check your device.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (filter === "unread") return !l.reviewed;
      if (filter === "contact") return l.lead_type === "contact";
      if (filter === "design") return l.lead_type === "design";
      return true;
    });
  }, [leads, filter]);

  const toggleReviewed = async (lead) => {
    const next = !lead.reviewed;
    // Optimistic update
    setLeads((cur) => cur.map((l) => (l.id === lead.id ? { ...l, reviewed: next } : l)));
    if (selected?.id === lead.id) setSelected({ ...selected, reviewed: next });
    try {
      await authClient.patch(`/api/admin/leads/${lead.id}`, { reviewed: next });
      fetchAll();
    } catch (e) {
      toast.error("Could not update lead status.");
      setLeads((cur) => cur.map((l) => (l.id === lead.id ? { ...l, reviewed: !next } : l)));
    }
  };

  const doLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-24 pb-16 grain">
      <div className="container-x px-5 md:px-10 lg:px-16">
        {/* Top bar */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">
              Owner dashboard
            </div>
            <h1 className="font-display text-3xl md:text-5xl mt-2">Hey, {user?.name || "Rob"}.</h1>
            <p className="text-sm text-[#A1A1AA] mt-2">
              Every quote request and design submission lands here — refreshed every 30 seconds and pushed instantly.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-go-to-website"
              className="btn-gold !py-2 !px-4 text-xs tracking-wide"
              title="Open the live website in a new tab"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Go to website
            </a>
            <button
              type="button"
              onClick={fetchAll}
              disabled={loading}
              data-testid="admin-refresh"
              className="btn-ghost !py-2 !px-4 text-xs"
              title="Refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              type="button"
              onClick={togglePush}
              disabled={pushBusy || !isPushSupported()}
              data-testid="admin-push-toggle"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-xs tracking-wide transition-colors ${
                pushOn
                  ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F3E5AB]"
                  : "border-[#27272A] text-[#D1D1D6] hover:border-[#AA8035]"
              } ${pushBusy || !isPushSupported() ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              {pushBusy ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : pushOn ? (
                <Bell className="w-3.5 h-3.5" />
              ) : (
                <BellOff className="w-3.5 h-3.5" />
              )}
              {pushOn ? "Push on" : "Push off"}
            </button>
            <button
              type="button"
              onClick={() => setShowSettings(true)}
              data-testid="admin-settings"
              className="btn-ghost !py-2 !px-4 text-xs"
            >
              <Lock className="w-3.5 h-3.5" /> Settings
            </button>
            <button
              type="button"
              onClick={doLogout}
              data-testid="admin-logout"
              className="btn-ghost !py-2 !px-4 text-xs"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
          <StatCard label="Unread" value={summary?.total_unread ?? 0} accent gold testid="stat-unread" />
          <StatCard
            label="Contact leads"
            value={summary?.contact?.total ?? 0}
            sub={`${summary?.contact?.last_7_days ?? 0} this week`}
            testid="stat-contact"
          />
          <StatCard
            label="Design requests"
            value={summary?.design?.total ?? 0}
            sub={`${summary?.design?.last_7_days ?? 0} this week`}
            testid="stat-design"
          />
          <StatCard
            label="This week total"
            value={(summary?.contact?.last_7_days ?? 0) + (summary?.design?.last_7_days ?? 0)}
            testid="stat-week"
          />
        </div>

        {/* Filter bar */}
        <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-3.5 h-3.5 text-[#D4AF37]" />
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                data-testid={`filter-${f.id}`}
                className={`px-4 py-1.5 rounded-full border text-xs transition-colors ${
                  filter === f.id
                    ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F3E5AB]"
                    : "border-[#27272A] text-[#D1D1D6] hover:border-[#AA8035]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="text-[10px] tracking-[0.24em] uppercase text-[#6b6b72]">
            {filtered.length} shown · {leads.length} total
          </div>
        </div>

        {/* Leads list */}
        <div className="mt-6 rounded-2xl border border-[#27272A] bg-[#0d0d0d] overflow-hidden">
          {loading && !leads.length ? (
            <div className="p-16 text-center text-[#A1A1AA]">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#D4AF37]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center text-[#A1A1AA]">
              <Inbox className="w-8 h-8 mx-auto mb-3 text-[#3f3f46]" />
              No leads to show. When a new one comes in, it'll appear here.
            </div>
          ) : (
            filtered.map((lead, i) => (
              <button
                key={lead.id}
                type="button"
                onClick={() => setSelected(lead)}
                data-testid={`lead-row-${lead.id}`}
                className={`w-full text-left px-5 md:px-7 py-4 md:py-5 flex items-start gap-4 border-b border-[#151517] last:border-b-0 hover:bg-[#111] transition-colors ${
                  !lead.reviewed ? "bg-[#D4AF37]/[0.03]" : ""
                }`}
              >
                <span
                  className={`mt-1 w-2 h-2 rounded-full flex-none ${
                    lead.reviewed ? "bg-[#3f3f46]" : "bg-[#D4AF37]"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div className="font-display text-lg md:text-xl text-white truncate">{lead.name}</div>
                    <div className="text-[10px] tracking-[0.24em] uppercase text-[#A1A1AA]">
                      {timeAgo(lead.created_at)}
                    </div>
                  </div>
                  <div className="text-xs text-[#A1A1AA] mt-1 flex items-center gap-3 flex-wrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] tracking-widest uppercase ${
                      lead.lead_type === "design"
                        ? "border-[#D4AF37]/40 text-[#F3E5AB] bg-[#D4AF37]/5"
                        : "border-[#3f3f46] text-[#D1D1D6]"
                    }`}>
                      {lead.lead_type === "design" ? <Sparkles className="w-2.5 h-2.5" /> : <Mail className="w-2.5 h-2.5" />}
                      {lead.lead_type === "design" ? "Design" : "Contact"}
                    </span>
                    <span>{lead.email}</span>
                    <span>{lead.phone}</span>
                  </div>
                  <div className="text-sm text-[#D1D1D6] mt-2 line-clamp-1">
                    {lead.lead_type === "design"
                      ? `${lead.selections?.layout || "Wall design"} · ${lead.project_address || ""}`
                      : lead.message}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {selected && (
        <LeadModal
          lead={selected}
          onClose={() => setSelected(null)}
          onToggleReviewed={() => toggleReviewed(selected)}
        />
      )}

      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} testPush={testPush} />}
    </div>
  );
}

function StatCard({ label, value, sub, accent, gold, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border p-5 bg-[#0d0d0d] ${
        gold ? "border-[#D4AF37]/40 bg-[#D4AF37]/5" : "border-[#27272A]"
      }`}
    >
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#A1A1AA]">{label}</div>
      <div
        className={`font-display mt-2 leading-none ${
          gold ? "text-[#F3E5AB] text-4xl" : "text-white text-3xl"
        }`}
      >
        {value}
      </div>
      {sub && <div className="text-[11px] text-[#6b6b72] mt-2">{sub}</div>}
    </div>
  );
}

function LeadModal({ lead, onClose, onToggleReviewed }) {
  const isDesign = lead.lead_type === "design";
  const s = lead.selections || {};
  const phoneDigits = (lead.phone || "").replace(/[^\d+]/g, "");
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-stretch md:items-center justify-center p-0 md:p-4"
      onClick={onClose}
      data-testid="lead-modal"
    >
      <div
        className="relative w-full md:max-w-2xl bg-[#0c0c0c] border border-[#27272A] md:rounded-2xl overflow-hidden md:my-8 flex flex-col max-h-[100dvh] md:max-h-[calc(100dvh-4rem)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 text-white/80 hover:text-[#D4AF37]"
          data-testid="lead-modal-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="p-5 md:p-8 border-b border-[#1a1a1a] flex-none">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] pr-10">
            {isDesign ? "Design My Wall submission" : "Contact form lead"} · #{lead.id.slice(0, 8).toUpperCase()}
          </div>
          <h2 className="font-display text-2xl md:text-3xl mt-2 break-words">{lead.name}</h2>

          {/* Contact chips — tappable call / text / email */}
          <div className="mt-4 flex flex-wrap gap-2">
            {phoneDigits && (
              <>
                <a
                  href={`tel:${phoneDigits}`}
                  data-testid="lead-call-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#F3E5AB] text-xs hover:bg-[#D4AF37]/20 transition-colors"
                >
                  <Phone className="w-3.5 h-3.5" /> Call {lead.phone}
                </a>
                <a
                  href={`sms:${phoneDigits}`}
                  data-testid="lead-text-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#27272A] text-[#D1D1D6] text-xs hover:border-[#AA8035] transition-colors"
                >
                  <MessageSquare className="w-3.5 h-3.5 text-[#D4AF37]" /> Text
                </a>
              </>
            )}
            {lead.email && (
              <a
                href={`mailto:${lead.email}`}
                data-testid="lead-email-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#27272A] text-[#D1D1D6] text-xs hover:border-[#AA8035] transition-colors break-all"
              >
                <Mail className="w-3.5 h-3.5 text-[#D4AF37]" /> {lead.email}
              </a>
            )}
            {lead.project_address && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#27272A] text-[#A1A1AA] text-xs">
                <MapPin className="w-3.5 h-3.5 text-[#D4AF37]" />
                {lead.project_address}
              </span>
            )}
          </div>
        </div>

        {/* Scrollable body */}
        <div className="p-5 md:p-8 space-y-4 text-sm overflow-y-auto flex-1 min-h-0">
          {isDesign ? (
            <>
              {s.location && <Row label="Location" value={s.location} />}
              {s.layout && <Row label="Layout" value={s.layout} />}
              {s.measurements && <Row label="Measurements" value={s.measurements} />}
              {s.style && <Row label="Style" value={s.style} />}
              {s.tv && <Row label="TV" value={s.tv} />}
              {s.mainFinish && <Row label="Main finish" value={s.mainFinish} />}
              {s.accent && <Row label="Accent" value={s.accent} />}
              {s.colors?.length ? <Row label="Colors" value={s.colors.join(", ")} /> : null}
              {s.trim && <Row label="Trim" value={s.trim} />}
              {s.fireplace && <Row label="Fireplace" value={s.fireplace} />}
              {s.console && <Row label="Console" value={s.console} />}
              {s.towers && <Row label="Towers" value={s.towers} />}
              {s.lighting && <Row label="Lighting" value={s.lighting} />}
              {s.equipment?.length ? <Row label="Equipment" value={s.equipment.join(", ")} /> : null}
              {s.budget && <Row label="Budget" value={s.budget} />}
              {s.timing && <Row label="Timing" value={s.timing} />}
              {s.helpMeChoose?.length ? <Row label="Needs guidance" value={s.helpMeChoose.join(", ")} /> : null}
              {lead.special_requests && (
                <div className="pt-4 border-t border-[#1a1a1a]">
                  <div className="text-[10px] tracking-[0.24em] uppercase text-[#D4AF37]">
                    Special requests
                  </div>
                  <div className="mt-2 text-[#D1D1D6] whitespace-pre-wrap break-words">{lead.special_requests}</div>
                </div>
              )}
              {(lead.current_wall_photo || lead.inspiration_photo) && (
                <div className="pt-4 border-t border-[#1a1a1a] grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {lead.current_wall_photo && (
                    <div>
                      <div className="text-[10px] tracking-[0.24em] uppercase text-[#D4AF37] mb-2">
                        Current wall
                      </div>
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${lead.current_wall_photo}`}
                        alt="Current wall"
                        className="w-full rounded-lg border border-[#27272A]"
                      />
                    </div>
                  )}
                  {lead.inspiration_photo && (
                    <div>
                      <div className="text-[10px] tracking-[0.24em] uppercase text-[#D4AF37] mb-2">
                        Inspiration
                      </div>
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${lead.inspiration_photo}`}
                        alt="Inspiration"
                        className="w-full rounded-lg border border-[#27272A]"
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              {lead.project_type && <Row label="Project type" value={lead.project_type} />}
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-[#D4AF37]">Message</div>
                <div className="mt-2 text-[#D1D1D6] whitespace-pre-wrap break-words">{lead.message}</div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 md:p-8 border-t border-[#1a1a1a] flex items-center justify-between gap-3 flex-wrap flex-none">
          <div className="text-[11px] text-[#6b6b72]">
            Received {new Date(lead.created_at).toLocaleString()}
          </div>
          <button
            type="button"
            onClick={onToggleReviewed}
            data-testid="lead-toggle-reviewed"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-xs transition-colors ${
              lead.reviewed
                ? "border-[#3f3f46] text-[#D1D1D6] hover:border-[#AA8035]"
                : "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F3E5AB]"
            }`}
          >
            {lead.reviewed ? <Circle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {lead.reviewed ? "Mark unread" : "Mark reviewed"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[140px,1fr] gap-1 sm:gap-4">
      <div className="text-[10px] tracking-[0.24em] uppercase text-[#6b6b72] sm:pt-0.5">{label}</div>
      <div className="text-[#D1D1D6] break-words min-w-0">{value}</div>
    </div>
  );
}

function SettingsModal({ onClose, testPush }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await authClient.post("/api/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated.");
      setCurrent("");
      setNext("");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg bg-[#0c0c0c] border border-[#27272A] rounded-2xl overflow-hidden my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-[#D4AF37]" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
        <div className="p-6 md:p-8 border-b border-[#1a1a1a]">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">Settings</div>
          <h2 className="font-display text-2xl mt-2">Account & notifications</h2>
        </div>
        <form onSubmit={submit} className="p-6 md:p-8 space-y-5">
          <div className="text-[10px] tracking-[0.24em] uppercase text-[#D4AF37]">Change password</div>
          <input
            className="input-dark"
            type="password"
            placeholder="Current password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            autoComplete="current-password"
            data-testid="settings-current-pw"
          />
          <input
            className="input-dark"
            type="password"
            placeholder="New password (min 8 chars)"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            autoComplete="new-password"
            data-testid="settings-new-pw"
          />
          <button type="submit" disabled={busy} className="btn-gold !py-3" data-testid="settings-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
          </button>
        </form>
        <div className="p-6 md:p-8 border-t border-[#1a1a1a]">
          <div className="text-[10px] tracking-[0.24em] uppercase text-[#D4AF37] mb-3">Push notifications</div>
          <button type="button" onClick={testPush} className="btn-ghost !py-2 !px-4 text-xs" data-testid="settings-test-push">
            <Send className="w-3.5 h-3.5" /> Send test push
          </button>
          <div className="text-[11px] text-[#6b6b72] mt-3">
            Rob will still receive an email at {SITE.email} for every new lead as a backup.
          </div>
        </div>
      </div>
    </div>
  );
}
